export type FolderItemType = 'VOCAB_ENTRY' | 'FUNCTIONAL_GRAMMAR_ENTRY' | 'SYSTEMIC_GRAMMAR_ENTRY' | 'DETAILED_CDS_ENTRY'
  | 'GLOBAL_CDS_ENTRY' | 'VERBAL_ACTION_ENTRY' | 'TEXT_TYPE' | 'TEXT_PATTERN';
export type FolderAccess = 'OWNER' | 'EDIT' | 'VIEW';
export type FolderPermission = 'EDIT' | 'VIEW';
export type FolderScope = 'ALL' | 'OWNED' | 'SHARED';
export type FolderSort = 'name' | 'createdAt' | 'updatedAt';

export interface FolderItem {
  type: FolderItemType;
  id: number;
}

export interface FolderUser {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

export interface SimpleFolder {
  id: number;
  name: string;
  owner: FolderUser;
  access: FolderAccess;
  itemCount: number;
  shareCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface FolderPage {
  content: SimpleFolder[];
  totalElements: number;
}

export interface EntryReference {
  id: number;
  name: string;
  example: string;
  categoryName: string;
  categoryId: number;
  receptive: string;
  productive: string;
  mode: string;
  activity: string;
  country: string;
}

export interface Folder {
  id: number;
  name: string;
  owner: FolderUser;
  access: FolderAccess;
  pending: boolean;
  itemCount: number;
  createdAt: string;
  updatedAt: string;
  vocabEntries: EntryReference[];
  functionalGrammarEntries: EntryReference[];
  systemicGrammarEntries: EntryReference[];
  textPatterns: EntryReference[];
  textTypes: EntryReference[];
  detailedCanDoStatementEntries: EntryReference[];
  globalCanDoStatementEntries: EntryReference[];
  verbalActionEntries: EntryReference[];
}

export type FolderEntryList = 'vocabEntries' | 'functionalGrammarEntries' | 'systemicGrammarEntries' | 'textPatterns'
  | 'textTypes' | 'detailedCanDoStatementEntries' | 'globalCanDoStatementEntries' | 'verbalActionEntries';

export interface FolderSave {
  name: string;
  items?: FolderItem[];
}

export interface FolderShare {
  id: number;
  /** the invited address */
  email: string;
  /** the recipient, only once the share is accepted */
  user: FolderUser | null;
  permission: FolderPermission;
  accepted: boolean;
  createdAt: string;
}

export interface FolderUsage {
  folderCount: number;
  userCount: number;
}

export type ExportFormat = 'XLSX' | 'PDF';
export type FolderExportSection = 'VOCAB' | 'FUNCTIONAL_GRAMMAR' | 'SYSTEMIC_GRAMMAR' | 'GLOBAL_CDS' | 'DETAILED_CDS'
  | 'VERBAL_ACTION' | 'TEXT_TYPE' | 'TEXT_PATTERN';

export interface ExportField {
  key: string;
  label: string;
  defaultSelected: boolean;
}

export interface ExportSection {
  section: FolderExportSection;
  label: string;
  fields: ExportField[];
}

export interface FolderExportRequest {
  format: ExportFormat;
  fields?: { [section in FolderExportSection]?: string[] };
}

export interface FolderItemTypeInfo {
  type: FolderItemType;
  label: string;
  list: FolderEntryList;
  exportSection: FolderExportSection;
}

/**
 * all item types in display order, with the list they are returned in and their export section
 */
export const FOLDER_ITEM_TYPES: FolderItemTypeInfo[] = [
  {type: 'VOCAB_ENTRY', label: 'Wortschatz', list: 'vocabEntries', exportSection: 'VOCAB'},
  {type: 'FUNCTIONAL_GRAMMAR_ENTRY', label: 'Funktionale Grammatik', list: 'functionalGrammarEntries', exportSection: 'FUNCTIONAL_GRAMMAR'},
  {type: 'SYSTEMIC_GRAMMAR_ENTRY', label: 'Systematische Grammatik', list: 'systemicGrammarEntries', exportSection: 'SYSTEMIC_GRAMMAR'},
  {type: 'GLOBAL_CDS_ENTRY', label: 'Globale Kannbeschreibungen', list: 'globalCanDoStatementEntries', exportSection: 'GLOBAL_CDS'},
  {type: 'DETAILED_CDS_ENTRY', label: 'Detaillierte Kannbeschreibungen', list: 'detailedCanDoStatementEntries', exportSection: 'DETAILED_CDS'},
  {type: 'VERBAL_ACTION_ENTRY', label: 'Sprachhandlungen', list: 'verbalActionEntries', exportSection: 'VERBAL_ACTION'},
  {type: 'TEXT_TYPE', label: 'Textsorten', list: 'textTypes', exportSection: 'TEXT_TYPE'},
  {type: 'TEXT_PATTERN', label: 'Textmuster', list: 'textPatterns', exportSection: 'TEXT_PATTERN'},
];

export const FOLDER_ACCESS_LABELS: { [access in FolderAccess]: string } = {
  OWNER: 'Eigentümer',
  EDIT: 'Bearbeiten',
  VIEW: 'Ansehen',
};

export const FOLDER_PERMISSION_LABELS: { [permission in FolderPermission]: string } = {
  EDIT: 'Bearbeiten',
  VIEW: 'Ansehen',
};

export function folderUserName(user: FolderUser): string {
  if (!user) {
    return '';
  }
  const name = [user.firstName, user.lastName].filter(n => !!n).join(' ');
  return name ? name + ' (' + user.email + ')' : user.email;
}
